import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import CandidateFollowup from "./Cc";
import RecentJobs from "./RecentJobs";
import { Card, CardHeader, CardTitle, CardContent } from "@/Components/ui/card";
import { Badge } from "@/Components/ui/badge";
import { Button } from "@/Components/ui/button";
import { Separator } from "@/Components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/Components/ui/dialog";
import {
  Building2, Briefcase, Users, CalendarCheck, Calendar, Mail,
  ChevronDown, FileText, Sparkles, Upload, AlertTriangle, FileSpreadsheet, Search
} from "lucide-react";
import { getApiUrl } from '../../utils/api';

const CompanyProfile = ({ activeMain, setActiveMain }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const companyID = new URLSearchParams(location.search).get("companyID");

  const [showDropdown, setShowDropdown] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [interviewScheduledToday, setInterviewScheduledToday] = useState(0);
  const [totalInterviewScheduled, setTotalInterviewScheduled] = useState(0);
  const [companyLoading, setCompanyLoading] = useState(true); // phase 1: company header
  const [statsLoading, setStatsLoading] = useState(true);     // phase 2: jobs + schedules
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [company, setCompany] = useState({});
  const [jobs, setJobs] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [includeRejectedInExport, setIncludeRejectedInExport] = useState(false);
  const [exportCompanyNameOptions, setExportCompanyNameOptions] = useState([]);
  const [loadingExportCompanyNameOptions, setLoadingExportCompanyNameOptions] = useState(false);
  const [selectedExportCompanyName, setSelectedExportCompanyName] = useState('');
  const [includeClosedRolesInExport, setIncludeClosedRolesInExport] = useState(false);
  const [exportRoleOptions, setExportRoleOptions] = useState([]);
  const [selectedExportRoleIds, setSelectedExportRoleIds] = useState([]);
  const [exportRoleSearchTerm, setExportRoleSearchTerm] = useState('');
  const [loadingExportRoles, setLoadingExportRoles] = useState(false);
  const [exportRolesLoaded, setExportRolesLoaded] = useState(false);
  const [exportDialogError, setExportDialogError] = useState('');

  const adminEmail = () =>
    sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email') || '';

  const normalizeCompanyName = (value) =>
    String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const isSearchJobCompany = (value) => {
    const normalized = normalizeCompanyName(value);
    return (
      normalized === 'searchjob' ||
      normalized === 'searchjobai' ||
      normalized.startsWith('searchjob')
    );
  };

  const isExportDialogSearchJob = isSearchJobCompany(company?.company_name);
  const filteredExportRoleOptions = exportRoleOptions.filter((role) =>
    role.title.toLowerCase().includes(exportRoleSearchTerm.trim().toLowerCase())
  );
  const areAllExportRolesSelected =
    exportRoleOptions.length > 0 &&
    exportRoleOptions.every((role) => selectedExportRoleIds.includes(role.jobId));

  const fetchCompanyByID = async (id) => {
    try {
      const response = await fetch(getApiUrl(`/api/get_company_by_ID?companyID=${encodeURIComponent(id)}`), {
        method: "GET", credentials: "include"
      });
      if (!response.ok) throw new Error("Failed to fetch company");
      const data = await response.json();
      setCompany(data);
      return data;
    } catch (error) {
      console.error("Error fetching company:", error);
      return null;
    }
  };

  const fetchUpcomingSchedule = async (companyEmail) => {
    if (!companyEmail) return;
    try {
      const response = await fetch(
        getApiUrl(`/api/company_upcoming_schedules?company_email=${encodeURIComponent(companyEmail)}`),
        { method: "POST", credentials: "include" }
      );
      if (!response.ok) return;
      const data = await response.json();
      setTotalInterviewScheduled(data.length);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      let todayCount = 0;
      const upcoming = data.filter(s => {
        const d = new Date(s.scheduled_time);
        if (d.toDateString() === today.toDateString()) todayCount++;
        return d >= today;
      });
      setInterviewScheduledToday(todayCount);
      setSchedules(upcoming);
    } catch (error) {
      console.error("Error fetching upcoming schedule:", error);
    }
  };

  const fetchJobs = async (companyEmail) => {
    if (!companyEmail) return;
    try {
      const response = await fetch(
        getApiUrl(`/api/get_jd?company_email=${encodeURIComponent(companyEmail)}`),
        { credentials: "include" }
      );
      if (!response.ok) return;
      const data = await response.json();
      setJobs(data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  useEffect(() => {
    if (!companyID) return;
    const load = async () => {
      // Phase 1 — fetch company (needed for email)
      setCompanyLoading(true);
      setStatsLoading(true);
      const fetchedCompany = await fetchCompanyByID(companyID);
      setCompanyLoading(false);

      // Phase 2 — fetch jobs + schedules IN PARALLEL
      if (fetchedCompany?.email) {
        await Promise.all([
          fetchJobs(fetchedCompany.email),
          fetchUpcomingSchedule(fetchedCompany.email),
        ]);
      }
      setStatsLoading(false);
    };
    load();
  }, [companyID]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.post-job-dropdown')) setShowDropdown(false);
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (activeMain === "delete-company-profile") setShowDeletePopup(true);
  }, [activeMain, location.state]);

  useEffect(() => {
    if (!exportDialogOpen || !isExportDialogSearchJob || !companyID) {
      setExportCompanyNameOptions([]);
      setLoadingExportCompanyNameOptions(false);
      return;
    }

    let cancelled = false;
    setLoadingExportCompanyNameOptions(true);

    const loadCompanyNameOptions = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
        };
        const currentAdminEmail = adminEmail();
        if (currentAdminEmail) {
          headers['X-Admin-Email'] = currentAdminEmail;
        }

        const response = await fetch(
          getApiUrl(`/api/admin/companies/${companyID}/export-company-name-options`),
          {
            method: 'GET',
            credentials: 'include',
            headers,
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load company-name options (${response.status})`);
        }

        const data = await response.json();
        if (cancelled) return;

        const seen = new Set();
        const nextOptions = [];
        (Array.isArray(data) ? data.map((item) => item?.company_name) : []).forEach((rawName) => {
          const name = String(rawName || '').trim();
          const key = name.toLowerCase();
          if (!name || seen.has(key)) return;
          seen.add(key);
          nextOptions.push(name);
        });

        nextOptions.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        setExportCompanyNameOptions(nextOptions);
        setSelectedExportCompanyName((prev) => {
          if (prev && nextOptions.some((name) => name.toLowerCase() === prev.toLowerCase())) {
            return prev;
          }
          if (nextOptions.length === 1) {
            return nextOptions[0];
          }
          return '';
        });
      } catch (error) {
        console.error('Failed to load export company-name options:', error);
        if (!cancelled) {
          setExportCompanyNameOptions([]);
          setSelectedExportCompanyName('');
        }
      } finally {
        if (!cancelled) {
          setLoadingExportCompanyNameOptions(false);
        }
      }
    };

    loadCompanyNameOptions();
    return () => {
      cancelled = true;
    };
  }, [companyID, exportDialogOpen, isExportDialogSearchJob]);

  useEffect(() => {
    if (
      !exportDialogOpen ||
      !companyID ||
      (isExportDialogSearchJob && !selectedExportCompanyName)
    ) {
      setExportRoleOptions([]);
      setSelectedExportRoleIds([]);
      setLoadingExportRoles(false);
      setExportRolesLoaded(false);
      return;
    }

    let cancelled = false;
    setLoadingExportRoles(true);
    setExportRolesLoaded(false);
    setExportDialogError('');

    const loadExportRoles = async () => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        const currentAdminEmail = adminEmail();
        if (currentAdminEmail) headers['X-Admin-Email'] = currentAdminEmail;

        const params = new URLSearchParams();
        if (includeClosedRolesInExport) params.set('includeClosedRoles', 'true');
        if (isExportDialogSearchJob) params.set('selectedCompanyName', selectedExportCompanyName);

        const response = await fetch(
          getApiUrl(`/api/admin/companies/${companyID}/export-role-options?${params.toString()}`),
          { method: 'GET', credentials: 'include', headers }
        );
        if (!response.ok) throw new Error(`Failed to load roles (${response.status})`);

        const data = await response.json();
        if (cancelled) return;
        const roles = (Array.isArray(data) ? data : [])
          .filter((role) => role?.job_id != null)
          .map((role) => ({
            jobId: String(role.job_id),
            title: String(role.job_title || `Requirement ${role.job_id}`).trim(),
            status: String(role.requirement_status || 'active').trim().toLowerCase(),
          }));
        setExportRoleOptions(roles);
        setSelectedExportRoleIds((previousIds) =>
          previousIds.filter((jobId) => roles.some((role) => role.jobId === jobId))
        );
        setExportRolesLoaded(true);
      } catch (error) {
        console.error('Failed to load export roles:', error);
        if (!cancelled) {
          setExportRoleOptions([]);
          setSelectedExportRoleIds([]);
          setExportDialogError('Unable to load roles for this export. Please try again.');
        }
      } finally {
        if (!cancelled) setLoadingExportRoles(false);
      }
    };

    loadExportRoles();
    return () => { cancelled = true; };
  }, [
    companyID,
    exportDialogOpen,
    includeClosedRolesInExport,
    isExportDialogSearchJob,
    selectedExportCompanyName,
  ]);

  // Post-job options must carry the selected company so the reused PostJobForm/
  // JdAi/UploadJD can post on that company's behalf via ?companyID=. Without it
  // these fell through to the /* catch-all and rendered NotFound (404).
  const companyQuery = companyID ? `?companyID=${encodeURIComponent(companyID)}` : '';
  const handleCreateJD = () => { navigate(`/create-JD${companyQuery}`); setShowDropdown(false); };
  const handleGenerateJD = () => { navigate(`/generate-jd${companyQuery}`); setShowDropdown(false); };
  const handleUploadJD = () => { navigate(`/upload-JD${companyQuery}`); setShowDropdown(false); };
  const handleClosePopup = () => { setShowDeletePopup(false); setActiveMain("company-profile"); };

  const sanitizeFilename = (name) => {
    const safe = (name || 'Company').replace(/[\\/*?:[\]]/g, '').trim().replace(/\s+/g, '_');
    return `${safe || 'Company'}_Requirements_Candidates.xlsx`;
  };

  const resetExportDialog = () => {
    setExportDialogOpen(false);
    setIncludeRejectedInExport(false);
    setExportCompanyNameOptions([]);
    setLoadingExportCompanyNameOptions(false);
    setSelectedExportCompanyName('');
    setIncludeClosedRolesInExport(false);
    setExportRoleOptions([]);
    setSelectedExportRoleIds([]);
    setExportRoleSearchTerm('');
    setLoadingExportRoles(false);
    setExportRolesLoaded(false);
    setExportDialogError('');
  };

  const handleExportExcel = () => {
    if (!companyID) return;
    setIncludeRejectedInExport(false);
    setExportCompanyNameOptions([]);
    setLoadingExportCompanyNameOptions(false);
    setSelectedExportCompanyName('');
    setIncludeClosedRolesInExport(false);
    setExportRoleOptions([]);
    setSelectedExportRoleIds([]);
    setExportRoleSearchTerm('');
    setLoadingExportRoles(false);
    setExportRolesLoaded(false);
    setExportDialogError('');
    setExportDialogOpen(true);
  };

  const toggleExportRole = (jobId) => {
    setSelectedExportRoleIds((previousIds) => (
      previousIds.includes(jobId)
        ? previousIds.filter((id) => id !== jobId)
        : [...previousIds, jobId]
    ));
  };

  const toggleAllExportRoles = () => {
    setSelectedExportRoleIds(
      areAllExportRolesSelected ? [] : exportRoleOptions.map((role) => role.jobId)
    );
  };

  const confirmExportExcel = async () => {
    if (!companyID) return;

    if (isExportDialogSearchJob && !selectedExportCompanyName) {
      setExportDialogError('Please select a company name for SearchJob export.');
      return;
    }

    setExportDialogError('');
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (includeRejectedInExport) {
        params.set('includeRejected', 'true');
      }
      if (isExportDialogSearchJob && selectedExportCompanyName) {
        params.set('selectedCompanyName', selectedExportCompanyName);
      }
      if (includeClosedRolesInExport) {
        params.set('includeClosedRoles', 'true');
      }
      selectedExportRoleIds.forEach((jobId) => params.append('jobId', jobId));

      const query = params.toString();
      const response = await fetch(
        getApiUrl(`/api/admin/companies/${companyID}/export-excel${query ? `?${query}` : ''}`),
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'X-Admin-Email': adminEmail() },
        }
      );
      if (!response.ok) {
        let message = 'Failed to export Excel';
        try {
          const err = await response.json();
          message = err.error || message;
        } catch {
          // ignore
        }
        throw new Error(message);
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fallbackName = isExportDialogSearchJob && selectedExportCompanyName
        ? `${company.company_name}_${selectedExportCompanyName}`
        : company.company_name;
      const filename = match?.[1] || sanitizeFilename(fallbackName);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      resetExportDialog();
    } catch (err) {
      console.error('Excel export failed:', err);
      setExportDialogError(err.message || 'Could not export Excel file.');
    } finally {
      setExportLoading(false);
    }
  };

  const convertToIST = (gmtDate) => {
    const date = new Date(gmtDate);
    return date.toLocaleString('en-IN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  };

  // T1-3: "New Applications" (was hardcoded 4) and "Selected Candidates" (was 8)
  // removed — the values weren't real. Only show metrics we can actually compute.
  const statsCards = [
    { title: "Active Jobs",         count: jobs.length,                               icon: Briefcase,    color: "bg-orange-50 text-orange-600" },
    { title: "Interviews Done",     count: totalInterviewScheduled - schedules.length, icon: CalendarCheck,color: "bg-cyan-50 text-cyan-600" },
    { title: "Interviews Today",    count: interviewScheduledToday,                    icon: Calendar,     color: "bg-green-50 text-green-600" },
  ];

  if (companyLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin h-10 w-10 border-4 border-slate-100 border-t-[#5d52d9] rounded-full" />
      </div>
    );
  }

  return (
    <div className="bg-slate-50 p-6 min-h-full">
      {/* Header — visible immediately after company loads */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#142DB7] flex items-center justify-center shrink-0">
            <span className="text-white text-xl font-bold">
              {company.company_name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{company.company_name}</h1>
            {company.email && (
              <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
                <Mail className="w-3.5 h-3.5" />
                {company.email}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={exportLoading}
            onClick={handleExportExcel}
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" />
            {exportLoading ? 'Exporting…' : 'Export Excel'}
          </Button>
          <div className="post-job-dropdown relative">
          <Button className="bg-[#5d52d9] hover:bg-[#7c6dee] text-white gap-2" onClick={() => setShowDropdown(!showDropdown)}>
            <FileText className="w-4 h-4" />
            Post Job
            <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </Button>
          {showDropdown && (
            <div className="absolute right-0 top-full mt-2 bg-white border border-slate-100 rounded-xl shadow-xl w-52 z-50 overflow-hidden">
              <button className="flex items-center gap-2 w-full text-left px-4 py-3 text-sm text-slate-700 bg-white hover:bg-slate-50 border-none cursor-pointer" onClick={handleCreateJD}>
                <FileText className="w-4 h-4 text-slate-400" /> Create JD
              </button>
              <button className="flex items-center gap-2 w-full text-left px-4 py-3 text-sm text-slate-700 bg-white hover:bg-slate-50 border-none cursor-pointer" onClick={handleGenerateJD}>
                <Sparkles className="w-4 h-4 text-slate-400" /> Generate JD with AI
              </button>
              <button className="flex items-center gap-2 w-full text-left px-4 py-3 text-sm text-slate-700 bg-white hover:bg-slate-50 border-none cursor-pointer" onClick={handleUploadJD}>
                <Upload className="w-4 h-4 text-slate-400" /> Upload JD
              </button>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Stats Cards — skeleton while jobs/schedules load */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {statsCards.map((s) => (
          <Card key={s.title} className="border border-slate-100 shadow-sm">
            <CardContent className="p-4">
              {statsLoading ? (
                <>
                  <div className="w-9 h-9 rounded-lg bg-slate-100 animate-pulse mb-3" />
                  <div className="h-7 w-10 bg-slate-100 rounded animate-pulse mb-1" />
                  <div className="h-3 w-20 bg-slate-100 rounded animate-pulse" />
                </>
              ) : (
                <>
                  <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center mb-3`}>
                    <s.icon className="w-4 h-4" />
                  </div>
                  <div className="text-2xl font-bold text-slate-800">{s.count}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.title}</div>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Upcoming Interviews */}
        <Card className="lg:col-span-2 border border-slate-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-indigo-500" />
              Upcoming Candidate Interviews
            </CardTitle>
          </CardHeader>
          <Separator />
          <CardContent className="pt-4 max-h-[360px] overflow-y-auto">
            {statsLoading ? (
              <div className="flex flex-col gap-3">
                {[1,2,3].map(i => (
                  <div key={i} className="flex gap-3 p-3 rounded-lg bg-slate-50 animate-pulse">
                    <div className="w-4 h-4 rounded bg-slate-200 mt-0.5 shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-3/4" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : schedules.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-8">No upcoming schedules available.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {schedules.map((schedule, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-indigo-50/40 border border-indigo-100">
                    <CalendarCheck className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-800">
                        {schedule.topic}{" "}
                        <Badge variant="outline" className="text-xs ml-1">
                          {schedule.test_type === "t" ? "Technical" : schedule.test_type === "p" ? "Personality" : schedule.test_type === "b" ? "Behavioral" : schedule.test_type === "c" ? "Coding" : "Test"}
                        </Badge>
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{schedule.full_names?.split(", ").join(" · ")}</p>
                      <p className="text-xs text-slate-400 mt-1">{convertToIST(schedule.scheduled_time)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Jobs */}
        <Card className="border border-slate-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-indigo-500" />
              Recent Jobs
            </CardTitle>
          </CardHeader>
          <Separator />
          <CardContent className="pt-4">
            {statsLoading ? (
              <div className="flex flex-col gap-2">
                {[1,2,3].map(i => <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />)}
              </div>
            ) : (
              <RecentJobs jobs={jobs} setActiveMain={setActiveMain} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Candidate Followup */}
      <div className="mt-4">
        <CandidateFollowup maxHeight={400} companyEmail={company.email} />
      </div>

      <Dialog open={exportDialogOpen} onOpenChange={(open) => { if (!open) resetExportDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Candidates Excel</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm text-slate-600">
                Choose export options for{' '}
                <span className="font-medium text-slate-900">
                  {company.company_name || 'this company'}
                </span>.
              </p>
              <p className="text-xs text-slate-500">
                By default, only non-rejected candidates are included.
              </p>
            </div>

            <label className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeRejectedInExport}
                onChange={(e) => setIncludeRejectedInExport(e.target.checked)}
                className="mt-0.5 rounded"
                disabled={exportLoading}
              />
              <div className="space-y-1">
                <span className="text-sm font-medium text-slate-900">
                  Include rejected candidate data
                </span>
                <p className="text-xs text-slate-500">
                  Turn this on only when you want rejected candidates in the export too.
                </p>
              </div>
            </label>

            {isExportDialogSearchJob && (
              <div className="space-y-2">
                <label htmlFor="company-export-company-name" className="text-sm font-medium text-slate-700">
                  Company Name
                </label>
                <select
                  id="company-export-company-name"
                  value={selectedExportCompanyName}
                  onChange={(e) => setSelectedExportCompanyName(e.target.value)}
                  disabled={loadingExportCompanyNameOptions || exportLoading}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                >
                  <option value="">
                    {loadingExportCompanyNameOptions ? 'Loading company names...' : 'Select company name'}
                  </option>
                  {exportCompanyNameOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500">
                  SearchJob exports require a company-name selection.
                </p>
                {!loadingExportCompanyNameOptions && exportCompanyNameOptions.length === 0 && (
                  <p className="text-xs text-amber-600">
                    No company-name options are available for this SearchJob export yet.
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">
                Roles{selectedExportRoleIds.length ? ` (${selectedExportRoleIds.length} selected)` : ''}
              </p>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeClosedRolesInExport}
                  onChange={(e) => setIncludeClosedRolesInExport(e.target.checked)}
                  className="rounded"
                  disabled={loadingExportRoles || exportLoading}
                />
                Include closed roles
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={areAllExportRolesSelected}
                  onChange={toggleAllExportRoles}
                  className="rounded"
                  disabled={
                    loadingExportRoles ||
                    exportLoading ||
                    !exportRolesLoaded ||
                    exportRoleOptions.length === 0 ||
                    (isExportDialogSearchJob && !selectedExportCompanyName)
                  }
                />
                Include all roles
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="search"
                  value={exportRoleSearchTerm}
                  onChange={(e) => setExportRoleSearchTerm(e.target.value)}
                  placeholder="Search roles..."
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  disabled={loadingExportRoles || exportLoading || !exportRolesLoaded}
                />
              </div>
              <div
                id="company-export-roles"
                className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100"
              >
                {loadingExportRoles && (
                  <p className="px-3 py-2 text-sm text-slate-500">Loading roles...</p>
                )}
                {!loadingExportRoles && exportRolesLoaded && filteredExportRoleOptions.length === 0 && (
                  <p className="px-3 py-2 text-sm text-slate-500">
                    {exportRoleOptions.length === 0 ? 'No active roles available' : 'No roles match your search'}
                  </p>
                )}
                {filteredExportRoleOptions.map((role) => (
                  <label
                    key={role.jobId}
                    className="flex items-center gap-3 px-3 py-2 text-sm text-slate-800 cursor-pointer hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedExportRoleIds.includes(role.jobId)}
                      onChange={() => toggleExportRole(role.jobId)}
                      disabled={
                        loadingExportRoles ||
                        exportLoading ||
                        !exportRolesLoaded ||
                        (isExportDialogSearchJob && !selectedExportCompanyName)
                      }
                      className="rounded"
                    />
                    <span>
                      {role.title}{role.status === 'closed' ? ' (Closed)' : ''}
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                Select roles to export only those roles. Include all roles to export every available role.
              </p>
            </div>

            {exportDialogError && (
              <p className="text-sm text-red-600">{exportDialogError}</p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={resetExportDialog} disabled={exportLoading}>
                Cancel
              </Button>
              <Button
                onClick={confirmExportExcel}
                disabled={
                  exportLoading ||
                  loadingExportRoles ||
                  !exportRolesLoaded ||
                  (isExportDialogSearchJob && (!selectedExportCompanyName || loadingExportCompanyNameOptions))
                }
                className="bg-[#5d52d9] hover:bg-[#7c6dee]"
              >
                {exportLoading ? 'Exporting…' : 'Export Excel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Company Dialog */}
      <Dialog open={showDeletePopup} onOpenChange={(open) => !open && handleClosePopup()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Company
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 text-sm">
            All data belonging to <strong>{company.company_name}</strong> will be permanently lost. Are you sure you want to delete this company?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={handleClosePopup}>Cancel</Button>
            <Button variant="destructive">Delete Company</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyProfile;
